import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import usePlayTimer from '@gameday/utils/hooks/usePlayTimer';
import { ChipFeedStaticWrapper } from '../Live/Live.style';
import playTypeMap from '../DynamicFieldView/utils/playTypeMap';
import { MotionDfvChipWrapper, DFVChipContainer } from './ChipFeed.style';
import { withTheme } from 'styled-components';
import SummaryPlayEvents from '../Play/play/SummaryPlayEvents';
import { AnimatePresence } from 'framer-motion';
import { getDynamicFieldViewLiveState } from '../DynamicFieldView/store/selectors/getDynamicFieldViewLive';
import { useSelector } from 'react-redux';

const EASE_OUT = 'easeOut';
const initial = { opacity: 0, y: '100%' };
const slideIn2Seconds = 1.875;
const easeOutSeconds = 0.375;
const animate = {
    opacity: [0, 1, 1, 1],
    y: [0, 0, 0, 110]
};
const exit = {
    opacity: 1,
    y: 110
};

const { PITCH_WITH_RESULT, PITCH, RESULT_NO_IN_PLAY, RESULT } = playTypeMap;
const EMPTY_OBJECT = Object.freeze({});

const displayPlayResult = ({ result, language }) => {
    const {
        event,
        description,
        eventType
    } = result;

    return (
        <SummaryPlayEvents
            styleType={'text'}
            isResult={true}
            language={ language }
            event={ event }
            description={ description }
            isLoading={ false }
            eventType={eventType}
            hideIcon />
    );
};

const ChipFeedInDfv = () => {
    const {
        currentPlay = {},
        locale = 'en',
        playType
    } = useSelector(getDynamicFieldViewLiveState);
    const { result = {} } = currentPlay;
    const [chipContent, setChipContent] = useState(null);
    const { timer, timerNum, delay } = usePlayTimer(true) || EMPTY_OBJECT;
    const { event, description } = result;

    const transition = useMemo(() => {
        const slideIn2Percentage = `${slideIn2Seconds / timerNum}`;
        const easeOutPercentage = `${(timerNum - easeOutSeconds) / timerNum}`;

        return {
            duration: timerNum,
            ease: EASE_OUT,
            times: [0, slideIn2Percentage, easeOutPercentage, 1],
            delay
        };
    }, [timerNum, delay]);

    useEffect(() => {
        let displayContent;
        const isResult = playType === PITCH_WITH_RESULT || playType === RESULT || playType === PITCH || playType === RESULT_NO_IN_PLAY;

        if (isResult && (event || description)) {
            displayContent = displayPlayResult({ result, language: locale });
        }

        setChipContent(displayContent);
    }, [playType, locale, currentPlay, timer, timerNum, event, description]);

    return (
        <AnimatePresence mode="wait">
            <ChipFeedStaticWrapper $playType={playType}>
                {chipContent
                    ? <DFVChipContainer $timer={timer} $playType={playType}>
                        <MotionDfvChipWrapper
                            key={'chip-feed-for-dfv-replay'}
                            initial={initial}
                            animate={animate}
                            exit={exit}
                            transition={transition}
                            $timer={timer}
                            $timerNum={timerNum}
                        >
                            {chipContent}
                        </MotionDfvChipWrapper>
                    </DFVChipContainer>
                    : null}
            </ChipFeedStaticWrapper>

        </AnimatePresence>
    );
};

ChipFeedInDfv.propTypes = {
    currentPlay: PropTypes.object,
    locale: PropTypes.string,
    playType: PropTypes.string,
    atBatIndex: PropTypes.number
};

export default withTheme(ChipFeedInDfv);
